'use client';

/**
 * CallScreen — the v3 voice-call overlay (design turn 11a). Replaces the old
 * inline VoiceOrbMessage: instead of a centered orb appearing inline in the
 * conversation scroll, the call now takes over the ENTIRE chat card as an
 * absolutely-positioned overlay (mounted by page.tsx, gated on `isCalling` —
 * see that file's AnimatePresence).
 *
 * [Turn 17, orb docking] Both layouts now have a PRE-DOCK and a DOCKED
 * shape — see `docked` below. Desktop: pre-dock is the orb centered across
 * the FULL overlay width (rail not mounted); docked is the split
 * pane — left column (logo, orb, status, controls) + right TranscriptRail
 * (passed `pinnedSlot={<PinnedCard />}` — part B). Mobile: pre-dock is a
 * full-screen centered column (orb + CallStatusCluster, no header, no
 * rail); docked is the transparent header (call-top-bar-status.tsx) +
 * TranscriptRail (same `pinnedSlot`), controls over a bottom gradient fade.
 *
 * The desktop/mobile split is a JS matchMedia check (not just responsive
 * Tailwind classes) so exactly ONE branch mounts — two `hidden sm:flex` /
 * `sm:hidden` subtrees would both still mount their contents (live rAF
 * loops in the hidden one).
 *
 * Turn 15 (rate-limit waiting UX) routes MODEL_BUSY notices differently by
 * layout: desktop keeps them in the existing "above controls" card zone
 * (rendered via CallNotice's PATIENCE/WARNING anatomy, same as every other
 * WARNING); mobile moves them OUT of the shared controls/top slots into
 * their own ribbon flush under the header (15f — see model-busy-ribbon.tsx).
 * Both surfaces also thread `modelBusy` into CallOrb (`dimmed`)/
 * CallStatusCluster|CallTopBarStatus (`resting`) so EXHAUSTED overrides the
 * orb/status glyph regardless of vizMode, and `orbReleaseSignal`/
 * `releasingNotice` so a recovery ("the exhale", 15e) animates the notice
 * out and replays the orb's one-shot release ripple instead of popping both
 * away instantly.
 *
 * [Turn 16] Mounted for the WHOLE call-start phase lifecycle (page.tsx gates
 * on `callPhase !== 'idle'`, not `isCalling`), so this component also owns
 * the `connecting`/`failed` treatments — the optimistic call-start restyle
 * (dial rings, the mic-wait/failure notice family, entrance choreography):
 *
 *   - The under-orb status glyph is a SHORT label (`statusOverride`):
 *     "Connecting…" (with the three blink dots, `statusOverrideDots`) while
 *     connecting; "Can't hear you" / "Couldn't connect" / "Reconnecting…"
 *     per failure reason — none of these carry the LONGER copy, which lives
 *     in the separate notice card below (call-start-notice.tsx).
 *   - CallOrb gets `connecting`/`reconnecting` (16a's three-ring dial /
 *     16c's calm single ring) or reuses its existing `dimmed` EXHAUSTED
 *     treatment for the two amber failure reasons — see that file for why
 *     `reconnecting` is its own thing, not `dimmed`.
 *   - The notice card slot renders CallStartNotice for the mic-wait
 *     sub-state and every `failed` reason; `mic_wait`'s exit plays the 15e
 *     "release downward" gesture instead of an abrupt unmount (see
 *     `micWaitReleasing` below). [Turn 17] These notices live in the SAME
 *     slot pre- and post-dock (the bottom controls wrapper on mobile; the
 *     left-column-relative absolute slot on desktop) — a post-dock mid-call
 *     failure (ws_closed reconnect, EXHAUSTED) renders in whichever layout
 *     (pre-dock centered / docked split) is currently active, per the Turn
 *     17 brief's regression note: docking never un-latches on a failure.
 *   - The whole surface's entrance (`OVERLAY_TRANSITION`) matches 16e's
 *     t16Enter timing/easing, honoring `prefers-reduced-motion` (single
 *     180ms opacity crossfade, no scale) via framer-motion's
 *     `useReducedMotion` — the established house pattern (see e.g.
 *     contact-summary-card.tsx).
 *
 * ── [Turn 17] Orb docking (design refs: t17-head-and-screen.html's
 * 17a/17b/17c, t17cd.html's 17d) ──────────────────────────────────────────
 *
 * Owner-locked decisions (17c):
 *   1. Dock on the FIRST CALL CONTENT, not on connect. `docked` is a LATCH
 *      (`useState` + an effect calling the pure `computeDocked` — see
 *      lib/orb-docking.ts) that flips true the instant the call is `live`
 *      AND `hasCallContent` (any finalized/streaming message in the call
 *      window — shared with transcript-rail.tsx's own selection via
 *      lib/call-transcript.ts, not re-derived here — or an active pinned
 *      card) is true. If content is ready the moment the call goes live,
 *      the latch flips on the very next render — "flows straight through".
 *   2. Stay docked for the rest of the call — `computeDocked` never
 *      returns false again once true; the latch only resets when this
 *      whole component unmounts (page.tsx gates the overlay on
 *      `callPhase !== 'idle'`, so a fresh call attempt is a fresh
 *      CallScreen instance — see call-phase.ts's file header). A post-dock
 *      `failed`(ws_closed)/EXHAUSTED still renders — in the DOCKED layout.
 *   3. Desktop docks the SAME way mobile does: the orb slides from the
 *      full-width center into the existing 42% left presence pane, and the
 *      transcript rail mounts on the right (17d).
 *
 * The dock plays as 17b's beat sequence, driven by TWO pieces of state:
 * `docked` (the pure latch — flips the instant first content is ready) and
 * `treeDocked` (what the render tree keys on — follows `docked` after
 * `LABEL_FADE_MS`). The gap between them IS beat 1: while
 * `docked && !treeDocked`, the pre-dock tree is still mounted and its
 * centered CallStatusCluster fades out via a plain opacity transition
 * (160ms ease-out — "nothing is travelling and dissolving at the same
 * time"); only then do the trees swap, which starts the travel. This
 * two-step swap exists because the obvious alternative — AnimatePresence
 * exit-fading the pre-dock tree while a `layoutId` child inside it is
 * mid-FLIP — is a fragile Framer combination (the exiting subtree can be
 * removed before the longer child animation finishes).
 *
 * The ONE traveling orb (`DockingOrb`, below) is a `layoutId`-tagged
 * `motion.div` — Framer's shared layout animation FLIPs its transform
 * (translate + scale, GPU-only) across the structural swap between the
 * pre-dock and docked trees, 620ms cubic-bezier(.45,.05,.2,1), starting at
 * the swap (= after the label fade). Desktop's CallControls ride the same
 * transition via their own position-only `layoutId` (they'd otherwise
 * teleport from full-width-center to the presence pane's center while the
 * orb glides). Reduced motion swaps ALL travel for plain 180ms opacity
 * crossfades between the two trees' instances (17b: "no travel"), and
 * skips the label-fade hold entirely.
 *
 * Docked content (transcript rail, header status/Live pill, mobile's
 * connected pill, desktop's pane status cluster) fades/rises in on mount
 * (`DOCK_CONTENT_TRANSITION`): 450ms ease-out, +200ms from travel start
 * (17b's "transcript rises" row). Desktop's rail enters via `x` (slides in
 * from the right, 17d); mobile's via `y` (rises 14px, 17b); the mobile
 * header's own label/pill entrance lives in call-top-bar-status.tsx
 * (`contentEntrance`). Controls wake (Mute/CC enabled) is driven by the
 * EXISTING `muteDisabled`/`ccDisabled` gate on CallControls — moved from
 * `!isLive` to `!treeDocked` (17b beat 4) — using that component's own
 * pre-existing dimmed→active transition rather than introducing a second,
 * bespoke crossfade duration for the same visual change.
 *
 * Backgrounds: mobile/desktop now render DIFFERENT radial gradients
 * (V3_CALL_OVERLAY_GRADIENTS, design-tokens.ts) — replaces the single
 * shared gradient this overlay used pre-Turn-17.
 */

import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Image from 'next/image';
import { useChatContext } from '@/contexts/ChatContext';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { cn } from '@/lib/utils';
import { CallOrb, type CallOrbProps } from './call-orb';
import { CallControls } from './call-controls';
import { CallNotice } from './call-notice';
import {
  CallStartNotice,
  type CallStartNoticeVariant,
} from './call-start-notice';
import { CallStatusCluster } from './call-status-cluster';
import { CallTopBarStatus } from './call-top-bar-status';
import { ModelBusyRibbon } from './model-busy-ribbon';
import { TranscriptRail } from './transcript-rail';
import { PinnedCard } from './pinned-card';
import {
  CallToolTimeoutPanel,
  TOOL_TIMEOUT_PANEL_RENDER_VIEWS,
} from './call-tool-timeout-panel';
import { resolveNoticeSlot } from '@/lib/call-notice';
import { hasCallContent, computeDocked } from '@/lib/orb-docking';
import { V3_CALL_OVERLAY_GRADIENTS } from '@/lib/design-tokens';
import { CallNoticeKind, VizMode } from '@/types/chat';
import type { CallFailureReason } from '@/lib/call-phase';

// [Turn 16, 16e] t16Enter: .5s cubic-bezier(.2,.7,.3,1), opacity 0→1 + scale
// .978→1. Reduced motion: "a single 180ms opacity crossfade, no scale" —
// handled by branching both the initial/exit scale AND this transition (see
// the component body's `reducedMotion` usage).
const OVERLAY_TRANSITION = { duration: 0.5, ease: [0.2, 0.7, 0.3, 1] as const };
const OVERLAY_TRANSITION_REDUCED = { duration: 0.18, ease: 'linear' as const };

// [Turn 16, 16a-alt] "the notice releases downward and fades (200ms) — same
// GESTURE as 15e recovery" — reusing the exact `call-notice-release`
// keyframe (300ms) rather than adding a near-duplicate at the literal 200ms
// (see design-tokens.ts's own "don't duplicate, reuse" convention); this
// timeout just needs to outlast that CSS animation so the DOM node isn't
// yanked mid-transition.
const MIC_WAIT_RELEASE_MS = 300;

// ── [Turn 17, 17b] Orb-docking motion constants ────────────────────────
//
// Beat 1: the centered label fades out for this long BEFORE the trees swap
// (see the `treeDocked` follower state) — travel never overlaps the label
// dissolving.
const LABEL_FADE_MS = 160;
// Orb FLIP: 620ms cubic-bezier(.45,.05,.2,1), starting at the tree swap
// (the label has already faded — no extra delay needed here).
const ORB_DOCK_TRANSITION = {
  duration: 0.62,
  ease: [0.45, 0.05, 0.2, 1] as const,
};
// Reduced motion: no FLIP at all — DockingOrb instead crossfades between the
// two differently-sized/positioned instances.
const ORB_DOCK_TRANSITION_REDUCED = { duration: 0.18, ease: 'linear' as const };
// Docked content's rise/slide-in — 450ms ease-out, +200ms from travel start
// (17b's "transcript rises" row; the swap IS travel start, so a plain 200ms
// delay lands the beat).
const DOCK_CONTENT_TRANSITION = {
  duration: 0.45,
  ease: 'easeOut' as const,
  delay: 0.2,
};
const DOCK_CONTENT_TRANSITION_REDUCED = {
  duration: 0.3,
  ease: 'easeOut' as const,
};

/** [Turn 17, 17b] The ONE orb that travels between CallScreen's pre-dock
 *  centered slot and its docked slot (mobile's 30px header orb via
 *  CallTopBarStatus's `orb` prop, or desktop's 128px presence-pane orb —
 *  see file header's "Deviation from 17b" note on why desktop doesn't
 *  shrink). Normal motion: a single `layoutId`-tagged element, so Framer's
 *  shared layout animation FLIPs it across the structural swap between the
 *  pre-dock/docked trees (both call sites below render exactly one
 *  `DockingOrb` instance at a time, matching `docked`). Reduced motion:
 *  doesn't FLIP — a plain opacity crossfade between the two instances,
 *  keyed on `docked` via AnimatePresence. */
function DockingOrb({
  docked,
  size,
  reducedMotion,
  ...orbProps
}: {
  docked: boolean;
  size: number;
  reducedMotion: boolean;
} & Omit<CallOrbProps, 'size' | 'className'>) {
  if (reducedMotion) {
    return (
      <AnimatePresence>
        <motion.div
          key={docked ? 'docked' : 'predock'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={ORB_DOCK_TRANSITION_REDUCED}
        >
          <CallOrb size={size} {...orbProps} />
        </motion.div>
      </AnimatePresence>
    );
  }
  return (
    <motion.div layoutId="call-orb-traveler" transition={ORB_DOCK_TRANSITION}>
      <CallOrb size={size} {...orbProps} />
    </motion.div>
  );
}

/** [Turn 16] "Connecting…" — the SAME short label for both the plain dial
 *  and the waiting-on-mic sub-state (only the notice card differs; see
 *  `noticeVariant` below and call-start-notice.tsx). */
function connectingStatusText(): string {
  return 'Connecting…';
}

/** [Turn 16] Short under-orb label per failure reason (16c) — the longer
 *  copy lives in the notice card (call-start-notice.tsx), not here. */
function failureStatusText(reason: CallFailureReason | null): string {
  switch (reason) {
    case 'mic_denied':
      return "Can't hear you";
    case 'ws_closed':
      return 'Reconnecting…';
    case 'network':
    case 'timeout':
    case 'backend':
    default:
      return "Couldn't connect";
  }
}

export function CallScreen() {
  const {
    vizMode,
    audioLevelsRef,
    toggleCall,
    isMuted,
    toggleMute,
    showCC,
    setShowCC,
    audioInputs,
    audioDevices,
    activeMicId,
    switchMic,
    callNotice,
    dismissCallNotice,
    modelBusy,
    releasingNotice,
    orbReleaseSignal,
    callPhase,
    callFailureReason,
    waitingOnMicPermission,
    retryCall,
    messages,
    pinnedCard,
    callStartMessageIndexRef,
    // Session-recovery: tool-timeout capture flow (design section 20b) —
    // the ONE shared flow instance (ChatContext composes useToolTimeoutFlow
    // exactly once) also driving text chat's ToolTimeoutCard — see that
    // hook's own header comment for why this can't be a separate instance
    // computed here (the transcript stays mounted underneath this overlay
    // for the whole call, so two independent instances could silently
    // disagree on the same incident).
    toolTimeoutFlow,
  } = useChatContext();
  const isDesktop = useIsDesktop();
  const reducedMotion = !!useReducedMotion();

  // [Turn 16] `live` is the only phase with a fully-live audio path; every
  // other phase (`connecting`/`ending`/`failed`) gets the status override +
  // dimmed/inert Mute+CC. End stays active throughout (handleEnd/toggleCall
  // are already phase-aware — see useCallMode.ts).
  const isLive = callPhase === 'live';
  const isConnecting = callPhase === 'connecting';
  const isFailed = callPhase === 'failed';
  // [Turn 16, 16c] The one failure reason that renders CALM, not amber —
  // see call-start-notice.tsx's file header for why this deviates from a
  // hard failure treatment.
  const isCalmReconnecting = isFailed && callFailureReason === 'ws_closed';

  // [Turn 17] The dock latch — see file header + lib/orb-docking.ts.
  // `contentReady` is transport-agnostic and shared with transcript-
  // rail.tsx's own selection (lib/call-transcript.ts), so this can never
  // disagree with what the rail is about to render.
  const contentReady = useMemo(
    () =>
      hasCallContent(messages, callStartMessageIndexRef.current, pinnedCard),
    [messages, callStartMessageIndexRef, pinnedCard]
  );
  const [docked, setDocked] = useState(false);
  useEffect(() => {
    setDocked((prev) => computeDocked(prev, callPhase, contentReady));
  }, [callPhase, contentReady]);
  // [Turn 17, 17b beat 1] `treeDocked` is what the render trees key on — it
  // follows `docked` after LABEL_FADE_MS so the centered label can fade out
  // BEFORE anything travels (the `labelFading` window below). Reduced motion
  // skips the hold: there's no travel to protect from overlapping a fade.
  const [treeDocked, setTreeDocked] = useState(false);
  useEffect(() => {
    if (!docked || treeDocked) return;
    if (reducedMotion) {
      setTreeDocked(true);
      return;
    }
    const timer = setTimeout(() => setTreeDocked(true), LABEL_FADE_MS);
    return () => clearTimeout(timer);
  }, [docked, treeDocked, reducedMotion]);
  const labelFading = docked && !treeDocked;

  // [Turn 17] Wall-clock captured once the call reaches `live` — feeds every
  // "Connected · HH:MM" chip (transcript-rail.tsx formats it). Effect-based
  // (not set inline during render) to mirror call-orb.tsx's own
  // prevModeRef-style "capture once on a genuine transition" pattern.
  const liveAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (isLive && liveAtRef.current === null) {
      liveAtRef.current = Date.now();
    }
  }, [isLive]);
  // Falls back to "now" rather than a blank/undefined time — by the time
  // anything actually renders this (post-dock, which requires `isLive`
  // already having been true for at least one prior render), the effect
  // above will already have run; the fallback only guards the
  // theoretical same-tick edge.
  const connectedAt = liveAtRef.current ?? Date.now();

  const statusOverride = isFailed
    ? failureStatusText(callFailureReason)
    : isConnecting
      ? connectingStatusText()
      : undefined;
  // Dots only for the plain connecting label (16a/16d) — no failed variant,
  // including the calm one, carries them (16c's mocks are dots-less).
  const statusOverrideDots = isConnecting;
  // The warm connecting/reconnecting tone covers both the dotted connecting
  // label AND the calm (dot-less) "Reconnecting…" one — see
  // call-status-cluster.tsx's Turn 16 note.
  const statusOverrideWarm = isConnecting || isCalmReconnecting;

  const handleEnd = () => {
    void toggleCall();
  };
  const handleToggleCC = () => setShowCC((prev) => !prev);
  const handleRetry = () => retryCall();

  // [Turn 16, 16a-alt] The mic-wait notice's exit isn't an abrupt unmount —
  // it "releases downward and fades" on GRANT, same gesture as 15e. There's
  // no equivalent snapshot for this in useCallMode (unlike `releasingNotice`
  // for MODEL_BUSY) since call-phase.ts's `waitingOnMicPermission` has no
  // concept of a release window of its own — this is a purely presentational
  // "hold the card visible a beat longer" concern, so it's local state here
  // rather than plumbing a new signal through the behavior half. Guarded on
  // the POST-transition phase being something other than `failed` — a
  // DENIAL also clears `waitingOnMicPermission` (useCallMode's
  // CANCEL_CONNECTING_WATCHDOG effect fires on every connecting EXIT, grant
  // or denial alike), and a denial must escalate straight to the amber
  // failed card, not play the calm release gesture first.
  const [micWaitReleasing, setMicWaitReleasing] = useState(false);
  const prevWaitingOnMicRef = useRef(waitingOnMicPermission);
  useEffect(() => {
    if (
      prevWaitingOnMicRef.current &&
      !waitingOnMicPermission &&
      callPhase !== 'failed'
    ) {
      setMicWaitReleasing(true);
      const timer = setTimeout(
        () => setMicWaitReleasing(false),
        MIC_WAIT_RELEASE_MS
      );
      prevWaitingOnMicRef.current = waitingOnMicPermission;
      return () => clearTimeout(timer);
    }
    prevWaitingOnMicRef.current = waitingOnMicPermission;
  }, [waitingOnMicPermission, callPhase]);

  // [Turn 16] Which CallStartNotice (if any) sits in the shared "above
  // controls" card slot — see that file for the four variants' copy/anatomy.
  const noticeVariant: CallStartNoticeVariant | null = isFailed
    ? isCalmReconnecting
      ? 'reconnecting'
      : callFailureReason === 'mic_denied'
        ? 'mic_denied'
        : 'unreachable'
    : waitingOnMicPermission || micWaitReleasing
      ? 'mic_wait'
      : null;
  // mic_wait has nothing to retry (the browser prompt IS the pending
  // action) — every other variant wires the SAME retryCall() the failed
  // surface's End-adjacent "Try again" always used.
  const noticeOnRetry = noticeVariant === 'mic_wait' ? undefined : handleRetry;

  // Turn 15e "the exhale": a MODEL_BUSY notice recovering doesn't vanish
  // the instant `callNotice` clears — useCallMode holds a snapshot in
  // `releasingNotice` for exactly the release-downward exit's duration, so
  // this falls back to it ONLY while `callNotice` itself is null. Every
  // other notice/topic never touches `releasingNotice` (stays null), so
  // this is a no-op fallback for them.
  const displayNotice = callNotice ?? releasingNotice;
  const isReleasingNotice = !callNotice && !!releasingNotice;
  const isExhausted = modelBusy === 'exhausted';
  // [Turn 16] `failed` reuses EXHAUSTED's exact orb treatment (opacity .5 +
  // saturate .75, every mode layer hidden) for every reason EXCEPT the calm
  // reconnecting one, which gets CallOrb's own quieter `reconnecting` prop
  // instead (opacity .7, no desaturation — "still working on its own", not
  // "stopped, resting"; see call-orb.tsx's Turn 16 note). [Turn 17] This
  // stays correct post-dock too — a mid-call ws_closed/EXHAUSTED renders in
  // whichever layout (pre-dock/docked) is currently active.
  //
  // Session-recovery (design section 20b): a tool-timeout capture flow in
  // progress reuses this EXACT same "held breath" register — see
  // CallToolTimeoutPanel's `onDimmedChange` doc comment for which of its
  // sub-views count as "still unresolved" vs. let the ordinary orb show
  // through. `toolTimeoutDimmed` is driven by that callback (not derived
  // inline here) because the panel owns the view machine that decides it.
  const [toolTimeoutDimmed, setToolTimeoutDimmed] = useState(false);
  const handleToolTimeoutDimmedChange = useCallback((next: boolean) => {
    setToolTimeoutDimmed(next);
  }, []);
  const orbDimmed =
    isExhausted || (isFailed && !isCalmReconnecting) || toolTimeoutDimmed;

  // Session-recovery: `toolTimeoutFlow` is null whenever no offer is
  // outstanding this session (live or restored) — same "nothing to show"
  // gate ToolTimeoutCard's mount in page.tsx uses, now reading the SAME
  // ChatContext field instead of each computing its own resolution. No
  // `key` needed: the flow's own toolName-reset (see useToolTimeoutFlow)
  // handles a fresh incident without a remount. `phoneOnFile` on the flow
  // is the ONLY phone-prefill source on a call — this is a browser-mic
  // voice session over WebRTC/WS, not a telephony call, so there is no
  // "number the user is dialing from" for the app to know; when no contact
  // record exists yet this conversation it's undefined and
  // ToolTimeoutCallback falls back to its normal empty field, same as text
  // chat.
  const toolTimeoutPanel = toolTimeoutFlow ? (
    <CallToolTimeoutPanel
      flow={toolTimeoutFlow}
      onDimmedChange={handleToolTimeoutDimmedChange}
    />
  ) : null;
  // session-recovery review Finding A: this can NOT be `!!toolTimeoutPanel`
  // — that tests REACT ELEMENT truthiness, which is true for every view
  // above (the component itself always returns a non-null element from
  // React's perspective), including the three views
  // (RETRYING/RECOVERED/COLLAPSED) CallToolTimeoutPanel renders `null` FOR
  // internally. Testing element truthiness meant that once ANY tool ever
  // timed out this session, this was permanently true — even after the
  // incident resolved — which made the `else` branch below (CallStartNotice/
  // CallNotice) permanently unreachable for the rest of the call. Gate on
  // the actual view instead — see TOOL_TIMEOUT_PANEL_RENDER_VIEWS's own doc
  // comment for why that's a positive (not negative) list.
  const toolTimeoutPanelVisible =
    !!toolTimeoutFlow &&
    TOOL_TIMEOUT_PANEL_RENDER_VIEWS.has(toolTimeoutFlow.state.view);
  // Priority rule for the shared "above controls" card slot (all three
  // layout sites below use THIS flag, not `toolTimeoutPanelVisible`
  // directly): a call-viability notice OUTRANKS the capture card.
  // `noticeVariant` (failed / reconnecting / mic_denied / mic_wait) means
  // the CALL itself is not functioning — the card's "Try again" is useless
  // in that state anyway (the backend forgets `pending_tool_retry` on
  // disconnect), so the retry-the-call affordance must win the slot;
  // otherwise an unresolved OFFER/FAILED card would suppress the
  // "call failed — tap to retry" notice forever, leaving a dead call with
  // only a tool-retry card. Advisory `CallNotice` warnings (rate-limit
  // etc.) deliberately do NOT preempt the card — the card can be an active
  // user flow (a half-filled form), and yanking it for a transient
  // advisory would lose typed input (the panel's sub-view local state
  // unmounts). Unmounting for `noticeVariant` CAN likewise lose typed form
  // input — accepted, because a dead call outranks a capture form.
  const toolTimeoutPanelWinsSlot = toolTimeoutPanelVisible && !noticeVariant;

  // Notice placement per the Turn-12 12b mockups (see resolveNoticeSlot's
  // doc comment in lib/call-notice.ts for the exact rules): success reads
  // near the transcript (desktop: over the rail top; mobile: dropping from
  // the very top — the error card shares that top slot on mobile); warnings
  // sit just above the controls on both layouts, where the fix (the mic
  // switcher) lives. Turn 15 (15f): MODEL_BUSY never uses these shared slots
  // on mobile — it gets its own ribbon under the header instead (rendered
  // below); desktop is unchanged, MODEL_BUSY still lands in the "above
  // controls" card zone alongside every other WARNING.
  const noticeSlot = resolveNoticeSlot(displayNotice, isDesktop);
  const controlsNotice = noticeSlot === 'controls' ? displayNotice : null;
  const topNotice = noticeSlot === 'top' ? displayNotice : null;
  const ribbonNotice = noticeSlot === 'ribbon' ? displayNotice : null;

  // [Turn 17, 17d] The Live pill — previously duplicated between this file
  // (mobile's CallTopBarStatus children) and transcript-rail.tsx's desktop
  // header; now defined once here, since desktop moved it OUT of the rail
  // header to float top-right of the whole overlay, so both surfaces read
  // from the same node.
  const livePill = (
    <span
      className={cn(
        'flex items-center gap-1.5 rounded-[20px] bg-[#E8F3EC] px-2.5 py-1 text-[12px] font-semibold text-[#1F8A5B]',
        !showCC && 'invisible'
      )}
    >
      {/* [FB35 review] The pre-unification desktop pill pulsed its dot
          whenever the call wasn't idle — restore that liveness cue. */}
      <span
        className={cn(
          'size-1.5 rounded-full bg-[#1F8A5B]',
          vizMode !== VizMode.IDLE && 'motion-safe:animate-pulse'
        )}
      />
      Live
    </span>
  );

  return (
    <motion.div
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.978 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.978 }}
      transition={
        reducedMotion ? OVERLAY_TRANSITION_REDUCED : OVERLAY_TRANSITION
      }
      className="absolute inset-0 z-30 overflow-hidden rounded-[inherit]"
      style={{
        // [Turn 17, 17a/17d] Two distinct gradients now, not one shared
        // background — see design-tokens.ts's V3_CALL_OVERLAY_GRADIENTS.
        background: isDesktop
          ? V3_CALL_OVERLAY_GRADIENTS.desktop
          : V3_CALL_OVERLAY_GRADIENTS.mobile,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Voice call with Sara"
    >
      {isDesktop ? (
        <div className="flex h-full">
          {treeDocked ? (
            <>
              {/* LEFT: presence pane (42%) — logo + orb + status + controls */}
              <div className="relative flex h-full flex-[0_0_42%] flex-col">
                <div className="flex h-16 shrink-0 items-center px-6">
                  <Image
                    src="/images/ic-logo.svg"
                    alt="The Art of Living"
                    width={104}
                    height={44}
                    className="h-7 w-auto"
                    priority
                  />
                </div>

                <div className="flex flex-1 flex-col items-center justify-center gap-6">
                  {/* [Turn 17] Desktop doesn't shrink the orb on dock (128px
                      both sides — see file header's deviation note): the
                      travel is pure translation, and the settled left pane
                      stays byte-identical to the shipped Turn-13/15 desktop. */}
                  <DockingOrb
                    docked={treeDocked}
                    size={128}
                    reducedMotion={reducedMotion}
                    mode={vizMode}
                    levelRef={audioLevelsRef}
                    dimmed={orbDimmed}
                    releaseSignal={orbReleaseSignal}
                    connecting={isConnecting}
                    reconnecting={isCalmReconnecting}
                  />
                  {/* [Turn 17, 17b] The pane's status cluster fades in on
                      the docked-content beat — the centered pre-dock label
                      already faded OUT before the swap (`labelFading`), so
                      without this entrance it would pop back mid-travel. */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={
                      reducedMotion
                        ? DOCK_CONTENT_TRANSITION_REDUCED
                        : DOCK_CONTENT_TRANSITION
                    }
                  >
                    <CallStatusCluster
                      vizMode={vizMode}
                      levelRef={audioLevelsRef}
                      resting={isExhausted || toolTimeoutDimmed}
                      statusOverride={statusOverride}
                      statusOverrideDots={statusOverrideDots}
                      statusOverrideWarm={statusOverrideWarm}
                    />
                  </motion.div>
                </div>

                {/* Session-recovery (design section 20b): shared "above
                    controls" card slot — one screen at a time, never two
                    stacked cards. See `toolTimeoutPanelWinsSlot` above for
                    the priority rule between the capture panel and the
                    notice cards. */}
                {toolTimeoutPanelWinsSlot ? (
                  <div className="absolute bottom-[116px] left-0 right-0 z-10 flex justify-center px-[16px]">
                    <div className="w-[340px]">{toolTimeoutPanel}</div>
                  </div>
                ) : (
                  <>
                    {noticeVariant && (
                      <div className="absolute bottom-[116px] left-0 right-0 z-10 flex justify-center px-[16px]">
                        <CallStartNotice
                          variant={noticeVariant}
                          onRetry={noticeOnRetry}
                          releasing={micWaitReleasing}
                          className="w-[340px]"
                        />
                      </div>
                    )}
                    {controlsNotice && (
                      <div className="absolute bottom-[116px] left-0 right-0 z-10 flex justify-center px-[16px]">
                        <CallNotice
                          notice={controlsNotice}
                          onDismiss={dismissCallNotice}
                          size="desktop"
                          releasing={isReleasingNotice}
                          className="w-[340px]"
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="flex shrink-0 justify-center pb-6">
                  {/* [Turn 17, 17d] Controls ride the dock: a position-only
                      layoutId glide (same 620ms path as the orb) from the
                      full-width center to the presence pane's center —
                      without it they'd teleport sideways while the orb
                      travels. The hugging flex-child wrapper is what gives
                      the FLIP a box that actually MOVES (CallControls' own
                      root spans its parent and merely re-centers). */}
                  <motion.div
                    layoutId={
                      reducedMotion ? undefined : 'call-controls-traveler'
                    }
                    layout={reducedMotion ? undefined : 'position'}
                    transition={ORB_DOCK_TRANSITION}
                  >
                    <CallControls
                      size="desktop"
                      isMuted={isMuted}
                      showCC={showCC}
                      onMute={toggleMute}
                      onEnd={handleEnd}
                      onToggleCC={handleToggleCC}
                      micDevices={audioInputs}
                      allDevices={audioDevices}
                      activeMicId={activeMicId}
                      onSelectMic={switchMic}
                      // [Turn 17] Moved from `!isLive` to `!treeDocked`
                      // (17b beat 4) — Mute/CC now wake as the orb docks,
                      // not merely when the call goes live.
                      muteDisabled={!treeDocked}
                      ccDisabled={!treeDocked}
                    />
                  </motion.div>
                </div>
              </div>

              {/* RIGHT: transcript rail — enters sliding in from the right
                  (17d), fading + translating in on mount only (no exit —
                  this tree only ever mounts once, when docking happens). */}
              <motion.div
                className="relative min-h-0 min-w-0 flex-1"
                initial={reducedMotion ? { opacity: 0 } : { opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={
                  reducedMotion
                    ? DOCK_CONTENT_TRANSITION_REDUCED
                    : DOCK_CONTENT_TRANSITION
                }
              >
                {/* [Turn 17, 17d] The Live pill rides the rail header's
                    right end (with "Connected · HH:MM") — see
                    TranscriptRail's `headerAccessory` doc for why it isn't
                    floated at the surface's top-right like the mock. */}
                <TranscriptRail
                  className="h-full"
                  pinnedSlot={<PinnedCard />}
                  connectedAt={connectedAt}
                  headerAccessory={livePill}
                />
              </motion.div>

              {/* 12c success placement (desktop): reads over the rail, just
                  under the Transcript header. Width matches the rail's
                  flex-1 share (left column is a fixed 42%). */}
              {topNotice && (
                <div className="pointer-events-none absolute right-0 top-[58px] z-10 flex w-[58%] justify-center">
                  <CallNotice
                    notice={topNotice}
                    size="desktop"
                    className="pointer-events-auto"
                  />
                </div>
              )}
            </>
          ) : (
            // [Turn 17] Pre-dock: orb centered across the FULL overlay
            // width — the rail isn't mounted/hidden at all yet.
            <div className="relative flex h-full w-full flex-col">
              <div className="flex h-16 shrink-0 items-center px-6">
                <Image
                  src="/images/ic-logo.svg"
                  alt="The Art of Living"
                  width={104}
                  height={44}
                  className="h-7 w-auto"
                  priority
                />
              </div>

              <div className="flex flex-1 flex-col items-center justify-center gap-6">
                <DockingOrb
                  docked={treeDocked}
                  size={128}
                  reducedMotion={reducedMotion}
                  mode={vizMode}
                  levelRef={audioLevelsRef}
                  dimmed={orbDimmed}
                  releaseSignal={orbReleaseSignal}
                  connecting={isConnecting}
                  reconnecting={isCalmReconnecting}
                />
                {/* [Turn 17, 17b beat 1] The centered label fades out
                    (160ms ease-out) BEFORE the trees swap and the orb
                    starts traveling — see `labelFading`. */}
                <div
                  className={cn(
                    'transition-opacity duration-[160ms] ease-out',
                    labelFading && 'opacity-[0]'
                  )}
                >
                  <CallStatusCluster
                    vizMode={vizMode}
                    levelRef={audioLevelsRef}
                    resting={isExhausted || toolTimeoutDimmed}
                    statusOverride={statusOverride}
                    statusOverrideDots={statusOverrideDots}
                    statusOverrideWarm={statusOverrideWarm}
                  />
                </div>
              </div>

              {/* Session-recovery (design section 20b) — see
                  `toolTimeoutPanelWinsSlot` above for the priority rule. */}
              {toolTimeoutPanelWinsSlot ? (
                <div className="absolute bottom-[116px] left-0 right-0 z-10 flex justify-center px-[16px]">
                  <div className="w-[340px]">{toolTimeoutPanel}</div>
                </div>
              ) : (
                <>
                  {noticeVariant && (
                    <div className="absolute bottom-[116px] left-0 right-0 z-10 flex justify-center px-[16px]">
                      <CallStartNotice
                        variant={noticeVariant}
                        onRetry={noticeOnRetry}
                        releasing={micWaitReleasing}
                        className="w-[340px]"
                      />
                    </div>
                  )}
                  {controlsNotice && (
                    <div className="absolute bottom-[116px] left-0 right-0 z-10 flex justify-center px-[16px]">
                      <CallNotice
                        notice={controlsNotice}
                        onDismiss={dismissCallNotice}
                        size="desktop"
                        releasing={isReleasingNotice}
                        className="w-[340px]"
                      />
                    </div>
                  )}
                </>
              )}
              {/* No rail to sit "over" yet — a full-width centered version
                  of the same 12c success placement. */}
              {topNotice && (
                <div className="pointer-events-none absolute left-0 right-0 top-[58px] z-10 flex justify-center">
                  <CallNotice
                    notice={topNotice}
                    size="desktop"
                    className="pointer-events-auto"
                  />
                </div>
              )}

              <div className="flex shrink-0 justify-center pb-6">
                {/* Same layoutId as the docked tree's wrapper — this is the
                    element the controls glide FROM on dock (see that
                    tree's comment). */}
                <motion.div
                  layoutId={
                    reducedMotion ? undefined : 'call-controls-traveler'
                  }
                  layout={reducedMotion ? undefined : 'position'}
                  transition={ORB_DOCK_TRANSITION}
                >
                  <CallControls
                    size="desktop"
                    isMuted={isMuted}
                    showCC={showCC}
                    onMute={toggleMute}
                    onEnd={handleEnd}
                    onToggleCC={handleToggleCC}
                    micDevices={audioInputs}
                    allDevices={audioDevices}
                    activeMicId={activeMicId}
                    onSelectMic={switchMic}
                    muteDisabled={!treeDocked}
                    ccDisabled={!treeDocked}
                    className="motion-safe:animate-call-rise motion-safe:[animation-delay:150ms]"
                  />
                </motion.div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex h-full flex-col">
          {/* [Turn 17] The docked transparent header only renders POST-dock
              — pre-dock has no top bar at all (see the centered layout
              below). Full parity (13d) — 30px orb + matching glyph, all
              state-driven; see CallTopBarStatus. The "Live" pill stays
              mounted (visibility-hidden, not conditionally rendered) when
              CC is off. */}
          {treeDocked && (
            <CallTopBarStatus
              vizMode={vizMode}
              levelRef={audioLevelsRef}
              dimmed={orbDimmed}
              releaseSignal={orbReleaseSignal}
              statusOverride={statusOverride}
              statusOverrideDots={statusOverrideDots}
              statusOverrideWarm={statusOverrideWarm}
              connecting={isConnecting}
              reconnecting={isCalmReconnecting}
              // [Turn 17, 17b] "Header status + Live pill fade in" on the
              // docked-content beat — without this the label/pill would pop
              // in at the swap while the orb is still traveling toward them.
              contentEntrance
              orb={
                <DockingOrb
                  docked={treeDocked}
                  size={30}
                  reducedMotion={reducedMotion}
                  mode={vizMode}
                  levelRef={audioLevelsRef}
                  dimmed={orbDimmed}
                  releaseSignal={orbReleaseSignal}
                  connecting={isConnecting}
                  reconnecting={isCalmReconnecting}
                />
              }
            >
              {livePill}
            </CallTopBarStatus>
          )}

          {/* Turn 15 (15f): MODEL_BUSY's own slim ribbon, flush under the
              header — never the shared controls/top slots below (see
              resolveNoticeSlot's `'ribbon'` case above). Recovery releases
              it downward the same way the desktop card does. */}
          {ribbonNotice && (
            <ModelBusyRibbon
              notice={ribbonNotice}
              releasing={isReleasingNotice}
            />
          )}

          {/* 12b success/error placement (mobile): drops from the top. */}
          {topNotice && (
            <div
              className={cn(
                'shrink-0 px-[14px] pt-[2px]',
                topNotice.kind === CallNoticeKind.SUCCESS &&
                  'flex justify-center'
              )}
            >
              <CallNotice
                notice={topNotice}
                onDismiss={
                  topNotice.kind === CallNoticeKind.SUCCESS
                    ? undefined
                    : dismissCallNotice
                }
                size="mobile"
              />
            </div>
          )}

          {treeDocked ? (
            // [Turn 17, 17b] Transcript rises in — 450ms ease-out, fades +
            // translates up 14px on mount (reduced motion: plain fade, no
            // rise). The rail owns its own min-h-0/min-w-0 containment
            // (FB26) — this wrapper adds the SAME contract at its own flex
            // level, since it's now the flex-1 child instead of the rail.
            <motion.div
              className="min-h-0 min-w-0 flex-1"
              initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={
                reducedMotion
                  ? DOCK_CONTENT_TRANSITION_REDUCED
                  : DOCK_CONTENT_TRANSITION
              }
            >
              <TranscriptRail
                className="h-full"
                pinnedSlot={<PinnedCard />}
                connectedAt={connectedAt}
              />
            </motion.div>
          ) : (
            // [Turn 17] Pre-dock: full-screen centered column per 16d/17a —
            // NO top bar, NO rail. CallStatusCluster (not CallTopBarStatus)
            // is the centered label block here.
            <div className="flex flex-1 flex-col items-center justify-center gap-6 px-[16px]">
              <DockingOrb
                docked={treeDocked}
                size={100}
                reducedMotion={reducedMotion}
                mode={vizMode}
                levelRef={audioLevelsRef}
                dimmed={orbDimmed}
                releaseSignal={orbReleaseSignal}
                connecting={isConnecting}
                reconnecting={isCalmReconnecting}
              />
              {/* [Turn 17, 17b beat 1] Same 160ms label fade-out before the
                  swap as desktop — see `labelFading`. */}
              <div
                className={cn(
                  'transition-opacity duration-[160ms] ease-out',
                  labelFading && 'opacity-[0]'
                )}
              >
                <CallStatusCluster
                  vizMode={vizMode}
                  levelRef={audioLevelsRef}
                  resting={isExhausted || toolTimeoutDimmed}
                  statusOverride={statusOverride}
                  statusOverrideDots={statusOverrideDots}
                  statusOverrideWarm={statusOverrideWarm}
                />
              </div>
            </div>
          )}

          <div className="flex shrink-0 flex-col items-center bg-gradient-to-t from-[#FBF7F1] via-[#FBF7F1]/45 to-transparent px-[14px] pb-6 pt-2.5">
            {/* Session-recovery (design section 20b) — same priority rule
                as the desktop slots above: see `toolTimeoutPanelWinsSlot`
                (a call-viability `noticeVariant` outranks the capture
                panel; advisory `controlsNotice` does not). */}
            {toolTimeoutPanelWinsSlot ? (
              <div className="mb-[14px] w-full">{toolTimeoutPanel}</div>
            ) : (
              <>
                {/* [Turn 16] mic-wait / failed notice — same slot the
                    controlsNotice card below uses (mutually exclusive with it
                    in practice). [Turn 17] Renders identically whether the call
                    is pre- or post-dock — a post-dock failure shows here too. */}
                {noticeVariant && (
                  <CallStartNotice
                    variant={noticeVariant}
                    onRetry={noticeOnRetry}
                    releasing={micWaitReleasing}
                    className="mb-[14px] w-full"
                  />
                )}
                {/* 12b warning placement (mobile): just above the controls,
                    over the gradient fade. */}
                {controlsNotice && (
                  <CallNotice
                    notice={controlsNotice}
                    onDismiss={dismissCallNotice}
                    size="mobile"
                    className="mb-[14px] w-full"
                  />
                )}
              </>
            )}
            <CallControls
              size="mobile"
              isMuted={isMuted}
              showCC={showCC}
              onMute={toggleMute}
              onEnd={handleEnd}
              onToggleCC={handleToggleCC}
              micDevices={audioInputs}
              allDevices={audioDevices}
              activeMicId={activeMicId}
              onSelectMic={switchMic}
              // [Turn 17] Moved from `!isLive` to `!treeDocked` (17b beat 4).
              muteDisabled={!treeDocked}
              ccDisabled={!treeDocked}
              className={cn(
                !treeDocked &&
                  'motion-safe:animate-call-rise motion-safe:[animation-delay:150ms]'
              )}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
}
